/** @module wgfractals/util/buffer */
import { getDevice } from "../init.mjs";

// Only what a fractal renderer needs: a uniform Params buffer written each
// frame, and a storage buffer the shader fills and we read back as pixels.

/**
 * Creates a GPU storage buffer with `COPY_SRC` so its contents can be copied
 * to a CPU-readable readback buffer after the shader runs.
 * @param {number} size - byte size
 * @param {string} [label] - debug label visible in browser DevTools GPU inspection
 * @returns {GPUBuffer}
 */
export function createResultBuffer(size, label = "fractal-result") {
  const device = getDevice();
  return device.createBuffer({
    label,
    size,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
  });
}

/**
 * Packs an array of typed scalar values into a uniform buffer aligned to 16 bytes.
 * Each entry specifies the value and its WGSL type (`"f32"`, `"u32"`, or `"i32"`).
 * The order of entries must match the field order in the shader's `Params` struct.
 * @param {{ value: number, type: "f32"|"u32"|"i32" }[]} params
 * @param {string} [label] - debug label visible in browser DevTools GPU inspection
 * @returns {GPUBuffer}
 */
export function createParamsBuffer(params, label = "fractal-params") {
  const device = getDevice();

  const rawSize = params.length * 4;
  const size = Math.ceil(rawSize / 16) * 16;

  const arrayBuffer = new ArrayBuffer(size);
  const view = new DataView(arrayBuffer);

  params.forEach(({ value, type }, i) => {
    const offset = i * 4;
    if (type === "u32") {
      view.setUint32(offset, value, true);
    } else if (type === "i32") {
      view.setInt32(offset, value, true);
    } else if (type === "f32") {
      view.setFloat32(offset, value, true);
    } else {
      throw new Error(`Unknown param type "${type}". Use "f32", "u32", or "i32".`);
    }
  });

  // UNIFORM: binds as var<uniform> in the shader; COPY_DST: allows writeBuffer to upload the packed data.
  const buffer = device.createBuffer({
    label,
    size,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });

  device.queue.writeBuffer(buffer, 0, arrayBuffer);

  return buffer;
}

/**
 * Appends a `copyBufferToBuffer` command to `commandEncoder` that copies
 * `sourceBuffer` into a new `MAP_READ` buffer. Returns that readback buffer;
 * call `readBuffer.mapAsync(GPUMapMode.READ)` after submitting the encoder.
 * @param {GPUCommandEncoder} commandEncoder
 * @param {GPUBuffer} sourceBuffer
 * @returns {GPUBuffer}
 */
export function stageReadback(commandEncoder, sourceBuffer) {
  const device = getDevice();

  const readBuffer = device.createBuffer({
    label: "fractal-readback",
    size: sourceBuffer.size,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
  });

  commandEncoder.copyBufferToBuffer(
    sourceBuffer, 0,
    readBuffer,   0,
    sourceBuffer.size,
  );

  return readBuffer;
}

/**
 * Destroys one or more GPU buffers. Accepts individual buffers or arrays of buffers.
 * @param {...(GPUBuffer|GPUBuffer[])} buffers
 */
export function destroyBuffers(...buffers) {
  buffers.flat().forEach((b) => b.destroy());
}
